/**
 * PartnerIntegrationDocs — copy-paste-able integration guidance for an
 * enterprise SSO partner. Rendered as the "Integration" tab inside
 * PartnerDetail, so a BuildIt staffer (super_admin) can share the
 * pre-filled snippets with the partner's first-line developer.
 *
 * Snippets are interpolated with this partner's `id` (UUID) and `slug`
 * (used as `iss`) so the staffer doesn't need to substitute IDs by hand.
 *
 * The Node.js reference implementation, error-code rows, and deep-link
 * back to the canonical guide are sourced from:
 *   /docs/integrations/enterprise-sso.md
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { CodeSnippet } from './CodeSnippet';

// Mirrors the row shape returned by enterprise_sso_partner_get RPC (see
// supabase/migrations/20260430120005_enterprise_sso_partner_rpcs.sql).
// `provisioning_mode` is `text` at the SQL level; we treat anything other
// than 'shared' as per-user wording for safety.
export interface IntegrationDocsPartner {
  id: string;
  name: string;
  slug: string;
  status: string;
  provisioning_mode: string;
  shared_workspace_id: string | null;
  default_member_role: string | null;
  portal_url: string | null;
  signing_secret_rotated_at: string | null;
  created_at: string;
  updated_at: string;
}

interface PartnerIntegrationDocsProps {
  partner: IntegrationDocsPartner;
}

interface ErrorRow {
  code: string;
  http: string;
  meaning: string;
}

const ERROR_ROWS: ErrorRow[] = [
  {
    code: 'invalid_token_format',
    http: '400',
    meaning:
      "Token couldn't be base64url-decoded, JSON-parsed, or shape validation rejected a field. Bug in your token builder — compare your output to the Fixture 1 canonical string in the integration guide.",
  },
  {
    code: 'unknown_partner',
    http: '401',
    meaning:
      "partner_id doesn't exist in BuildIt. Verify the partner_id you were assigned at provisioning matches what you put in the payload.",
  },
  {
    code: 'partner_disabled',
    http: '401',
    meaning:
      'BuildIt has suspended your integration. Contact BuildIt support.',
  },
  {
    code: 'bad_signature',
    http: '401',
    meaning:
      'HMAC verification failed. Two common causes: (1) you used a locale-aware lowercase on email; (2) you fed the secret hex string to HMAC instead of decoding to raw bytes first. Run Fixture 2 through your canonicalizer and diff against the guide.',
  },
  {
    code: 'expired',
    http: '401',
    meaning:
      'exp ≤ now, OR iat > now + 60s (clock skew tolerance), OR exp - iat > 600s (10-min lifetime cap). Generate a fresh token at click time; do not pre-mint.',
  },
  {
    code: 'replay',
    http: '401',
    meaning:
      'This nonce has already been used. Replay window is at least 1 hour. Generate a fresh nonce (UUIDv4) per token. Never reuse.',
  },
  {
    code: 'identity_revoked',
    http: '401',
    meaning:
      "This user's enterprise SSO access has been revoked on the BuildIt side. Contact your BuildIt-side admin; the user's (partner_id, external_user_id) mapping has been disabled.",
  },
  {
    code: 'shared_workspace_deleted',
    http: '401',
    meaning:
      'The shared workspace your tenancy was bound to has been removed. Contact BuildIt support to remap or recreate.',
  },
  {
    code: 'generate_link_failed',
    http: '500',
    meaning:
      'Internal error during user provisioning (Supabase Auth admin call failed). Retry once. If persistent, contact support.',
  },
  {
    code: 'finalize_failed',
    http: '500',
    meaning:
      'Partner identity insert/finalize step rolled back. Retry once. If persistent, contact support.',
  },
  {
    code: 'session_mint_failed',
    http: '500',
    meaning:
      'Session couldn’t be created after successful provisioning. Retry once. If persistent, contact support.',
  },
  {
    code: 'rate_limited',
    http: '429',
    meaning:
      'Too many attempts. Per-IP and per-(partner, IP) limits apply. Back off (exponential, starting at 1s). The limit is generous for normal portal traffic; persistent 429s indicate a bug.',
  },
  {
    code: 'internal_error',
    http: '500',
    meaning:
      'Unexpected error not covered above. Contact support if persistent.',
  },
];

const FULL_GUIDE_URL =
  'https://github.com/eliasonAdvising/built-it-beta/blob/main/docs/integrations/enterprise-sso.md';

const AUDIENCE = 'app.usebuildit.com';

function buildNodeSnippet(slug: string, partnerId: string): string {
  return `// How to run: BUILDIT_SSO_SECRET=<hex> node sso.js
const crypto = require('crypto');

function asciiLowercase(s) {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    out += c >= 0x41 && c <= 0x5A ? String.fromCharCode(c + 0x20) : s[i];
  }
  return out;
}

function buildToken(payload, secretHex) {
  const email = asciiLowercase(payload.email).normalize('NFC');
  const eid = payload.external_user_id.normalize('NFC');
  const canonical =
    \`v=\${payload.v}|aud=\${payload.aud}|iss=\${payload.iss}|iat=\${payload.iat}|exp=\${payload.exp}|\` +
    \`partner_id=\${payload.partner_id}|external_user_id=\${eid}|email=\${email}|nonce=\${payload.nonce}\`;
  const sig = crypto.createHmac('sha256', Buffer.from(secretHex, 'hex'))
    .update(canonical, 'utf8').digest('hex');
  const token = Buffer.from(JSON.stringify({ payload, sig })).toString('base64url');
  return { sig, token, url: \`https://${AUDIENCE}/auto-login?token=\${token}\` };
}

// Fill in per-user fields (external_user_id, email) at click time.
const now = Math.floor(Date.now() / 1000);
const payload = {
  v: 1,
  aud: '${AUDIENCE}',
  iss: '${slug}',
  iat: now,
  exp: now + 300,
  partner_id: '${partnerId}',
  external_user_id: 'emp-12345',
  email: 'alice@example.com',
  nonce: crypto.randomUUID(),
};

const { url } = buildToken(payload, process.env.BUILDIT_SSO_SECRET);
console.log(url);
`;
}

function buildCurlSnippet(slug: string, partnerId: string): string {
  return `# Build a token with sso.js, then POST to the auto-login endpoint.
# (POST keeps the token out of HTTP referer headers and browser history.)
TOKEN=$(BUILDIT_SSO_SECRET=<your-secret-hex> node sso.js | sed 's|.*token=||')
curl -i -X POST "https://${AUDIENCE}/auto-login?token=$TOKEN" \\
  -H "X-Partner-Slug: ${slug}" \\
  -H "X-Partner-Id: ${partnerId}"
`;
}

export function PartnerIntegrationDocs({
  partner,
}: PartnerIntegrationDocsProps): JSX.Element {
  const isShared: boolean = partner.provisioning_mode === 'shared';

  const overviewProse = isShared
    ? `${partner.name} is configured for shared-workspace provisioning. Every employee that signs in via SSO joins one BuildIt workspace, so they collaborate on the same contacts, campaigns, and inbox. Your portal mints a short-lived HMAC-SHA256 signed token per user click and sends the user's browser to /auto-login. BuildIt verifies the signature, looks up or provisions the BuildIt user mapped to (partner_id, external_user_id), and lands them inside the shared workspace.`
    : `${partner.name} is configured for per-user-workspace provisioning. Each employee that signs in via SSO gets their own BuildIt workspace — useful for independent agents or financial advisors who manage their own contact books. Your portal mints a short-lived HMAC-SHA256 signed token per user click and sends the user's browser to /auto-login. BuildIt verifies the signature, looks up or provisions the BuildIt user (and their workspace, on first sign-in) mapped to (partner_id, external_user_id), and lands them inside it.`;

  const nodeSnippet = buildNodeSnippet(partner.slug, partner.id);
  const curlSnippet = buildCurlSnippet(partner.slug, partner.id);

  return (
    <div className="space-y-6">
      {/* 1. How this works */}
      <section>
        <h3 className="text-sm font-semibold mb-2">How this works</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {overviewProse}
        </p>
      </section>

      {/* 2. Partner credentials */}
      <section>
        <h3 className="text-sm font-semibold mb-2">Partner credentials</h3>
        <dl className="grid gap-3">
          <CredentialRow label="partner_id">
            <CodeSnippet code={partner.id} />
          </CredentialRow>
          <CredentialRow label="iss (slug)">
            <CodeSnippet code={partner.slug} />
          </CredentialRow>
          <CredentialRow label="aud">
            <CodeSnippet code={AUDIENCE} />
          </CredentialRow>
          <CredentialRow label="Signing secret">
            <p className="text-sm italic text-muted-foreground">
              Issued at provisioning time. Rotate it from the Danger Zone tab
              if compromised. BuildIt cannot recover a lost secret.
            </p>
          </CredentialRow>
        </dl>
      </section>

      {/* 3. Node.js reference impl */}
      <section>
        <h3 className="text-sm font-semibold mb-2">
          Node.js reference implementation
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Drop this into <code className="font-mono text-xs">sso.js</code>,
          set <code className="font-mono text-xs">BUILDIT_SSO_SECRET</code> in
          the environment, and run with{' '}
          <code className="font-mono text-xs">node sso.js</code>. The signing
          secret is read from the environment — BuildIt never re-reads it.
        </p>
        <CodeSnippet
          code={nodeSnippet}
          language="javascript"
          label="sso.js"
        />
      </section>

      {/* 4. Direct test curl */}
      <section>
        <h3 className="text-sm font-semibold mb-2">
          Direct test (curl)
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Build a token with the script above, then POST it to the auto-login
          endpoint to verify end-to-end before wiring up the portal button.
        </p>
        <CodeSnippet code={curlSnippet} language="bash" label="bash" wrap />
      </section>

      {/* 5. Error code reference table */}
      <section>
        <h3 className="text-sm font-semibold mb-2">Error code reference</h3>
        <p className="text-sm text-muted-foreground mb-2">
          When verification fails, the auto-login page shows a generic error
          to the end user but logs an{' '}
          <code className="font-mono text-xs">error_code</code> against this
          partner record. Use these codes to triage with BuildIt support.
        </p>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Code</TableHead>
                <TableHead className="w-[80px]">HTTP</TableHead>
                <TableHead>Meaning / Fix</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ERROR_ROWS.map((row) => (
                <TableRow key={row.code}>
                  <TableCell className="font-mono text-xs align-top">
                    {row.code}
                  </TableCell>
                  <TableCell className="font-mono text-xs align-top">
                    {row.http}
                  </TableCell>
                  <TableCell className="text-sm align-top">
                    {row.meaning}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* 6. Deep-link callout */}
      <section>
        <div className="rounded-md border bg-muted/30 p-4">
          <p className="text-sm">
            <span className="font-medium">Need more?</span> The full
            integration guide covers all 5 reference languages
            (Python/Ruby/PHP/Go/Node), the byte-precise wire format, both
            fixture vectors, and the secret-rotation procedure.
          </p>
          <a
            href={FULL_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Open the full integration guide →
          </a>
        </div>
      </section>
    </div>
  );
}

function CredentialRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[160px_1fr] gap-3 items-start">
      <dt className="text-sm text-muted-foreground pt-2">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
